import { useLocale } from 'next-intl';
import { useCallback, useRef } from 'react';

import { trackingManager } from '@/lib/tracking/tracking-manager-instance';

interface VideoTrackingOptions {
  videoId: string;
  section?: string;
}

const VIDEO_MILESTONES = [25, 50, 75] as const;

export function useLandingVideoTracking({ videoId, section = 'hero' }: VideoTrackingOptions) {
  const locale = useLocale();
  const startTimeRef = useRef<number>(0);
  const videoDurationRef = useRef<number>(0);
  const reachedMilestonesRef = useRef<Set<number>>(new Set());

  const trackModalOpened = useCallback(() => {
    startTimeRef.current = Date.now();
    trackingManager.trackEvent('LANDING_VIDEO_INTERACTION', {
      action: 'modal_opened',
      videoId,
      section,
      playbackPosition: 0,
      watchDuration: 0,
      completionPercentage: 0,
      language: locale,
    });
  }, [section, videoId, locale]);

  const trackVideoPlayed = useCallback(
    (playbackPosition: number) => {
      if (startTimeRef.current === 0) {
        startTimeRef.current = Date.now();
      }
      trackingManager.trackEvent('LANDING_VIDEO_INTERACTION', {
        action: 'played',
        videoId,
        section,
        playbackPosition: Math.round(playbackPosition),
        watchDuration: 0,
        completionPercentage: 0,
        language: locale,
      });
    },
    [section, videoId, locale],
  );

  const trackVideoPaused = useCallback(
    (playbackPosition: number) => {
      const watchDuration = startTimeRef.current
        ? Math.round((Date.now() - startTimeRef.current) / 1000)
        : 0;
      trackingManager.trackEvent('LANDING_VIDEO_INTERACTION', {
        action: 'paused',
        videoId,
        section,
        playbackPosition: Math.round(playbackPosition),
        watchDuration,
        completionPercentage: 0,
        language: locale,
      });
    },
    [section, videoId, locale],
  );

  const trackVideoCompleted = useCallback(() => {
    const watchDuration = startTimeRef.current
      ? Math.round((Date.now() - startTimeRef.current) / 1000)
      : 0;
    trackingManager.trackEvent('LANDING_VIDEO_INTERACTION', {
      action: 'completed',
      videoId,
      section,
      playbackPosition: 0,
      watchDuration,
      completionPercentage: 100,
      language: locale,
    });
  }, [section, videoId, locale]);

  const trackModalClosed = useCallback(
    (currentTime: number) => {
      const watchDuration = startTimeRef.current
        ? Math.round((Date.now() - startTimeRef.current) / 1000)
        : 0;
      const completionPercentage =
        videoDurationRef.current > 0
          ? Math.round((currentTime / videoDurationRef.current) * 100)
          : 0;

      trackingManager.trackEvent('LANDING_VIDEO_INTERACTION', {
        action: 'modal_closed',
        videoId,
        section,
        playbackPosition: Math.round(currentTime),
        watchDuration,
        completionPercentage,
        language: locale,
      });

      // Reset for next session
      startTimeRef.current = 0;
      reachedMilestonesRef.current.clear();
    },
    [section, videoId, locale],
  );

  const setVideoDuration = useCallback((duration: number) => {
    videoDurationRef.current = duration;
  }, []);

  const trackMilestone = useCallback(
    (currentTime: number) => {
      if (videoDurationRef.current <= 0) return;

      const currentPercentage = (currentTime / videoDurationRef.current) * 100;
      const watchDuration = startTimeRef.current
        ? Math.round((Date.now() - startTimeRef.current) / 1000)
        : 0;

      for (const milestone of VIDEO_MILESTONES) {
        if (currentPercentage >= milestone && !reachedMilestonesRef.current.has(milestone)) {
          reachedMilestonesRef.current.add(milestone);
          trackingManager.trackEvent('LANDING_VIDEO_INTERACTION', {
            action: 'milestone_reached',
            videoId,
            section,
            playbackPosition: Math.round(currentTime),
            watchDuration,
            completionPercentage: milestone,
            language: locale,
          });
        }
      }
    },
    [videoId, section, locale],
  );

  // Provide event handlers that can be attached directly to video element
  const getVideoEventHandlers = useCallback(
    () => ({
      onPlay: (e: React.SyntheticEvent<HTMLVideoElement>) => {
        trackVideoPlayed(e.currentTarget.currentTime);
      },
      onPause: (e: React.SyntheticEvent<HTMLVideoElement>) => {
        trackVideoPaused(e.currentTarget.currentTime);
      },
      onEnded: () => {
        trackVideoCompleted();
      },
      onLoadedMetadata: (e: React.SyntheticEvent<HTMLVideoElement>) => {
        setVideoDuration(e.currentTarget.duration);
      },
      onTimeUpdate: (e: React.SyntheticEvent<HTMLVideoElement>) => {
        trackMilestone(e.currentTarget.currentTime);
      },
    }),
    [trackVideoPlayed, trackVideoPaused, trackVideoCompleted, setVideoDuration, trackMilestone],
  );

  return {
    trackModalOpened,
    trackModalClosed,
    trackVideoPlayed,
    trackVideoPaused,
    trackVideoCompleted,
    setVideoDuration,
    getVideoEventHandlers,
  };
}
